// src/pages/ProductDetail/ProductDetail.jsx
// FRONT OFFICE - Product detail page with Firebase integration

import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FaShoppingCart, FaRegFileAlt } from "react-icons/fa";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import { getProductById, getRelatedProducts } from "../../services/ProductService";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../contexts/AuthContext";
import "./ProductDetail.css";
import { getCategoryById } from "../../services/CategoryService";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");
  const { addProductToCart, loading: cartLoading } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadProductData = async () => {
      try {
        setLoading(true);
        
        // Fetch product details
        const productData = await getProductById(id);
        setProduct(productData);
        
        // Fetch related products
        if (productData && productData.categoryId) {
          const related = await getRelatedProducts(id, productData.categoryId);
          setRelatedProducts(related);

        console.log("get product productData related : productData ",productData);

          const cat =await getCategoryById(productData.categoryId);
          setCategory(cat);

        console.log("get product productData related : ",cat);
        }
      } catch (error) {
        console.error("Error loading product:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProductData();
    // Always reset scroll position when product changes
    window.scrollTo(0, 0);
  }, [id]);

  const handleQuantityChange = (e) => {
    const newQuantity = parseInt(e.target.value, 10);
    if (newQuantity > 0) {
      setQuantity(newQuantity);
    }
  };

  const incrementQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const handleAddToCart = async () => {
    if (!currentUser) {
      // If user is not logged in, redirect to login page
      navigate('/login', { 
        state: { 
          from: `/product/${id}`,
          message: 'Please log in to add items to your cart' 
        } 
      });
      return;
    }
    
    if (product) {
      try {
        await addProductToCart(id, quantity);
        // Show success message or open cart sidebar
      } catch (error) {
        console.error("Error adding to cart:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <div className="container">
          <h2>Product Not Found</h2>
          <p>
            The product you are looking for is not available or has been
            removed.
          </p>
          <Link to="/shop" className="btn btn-primary">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  // Fallback image if product image is not available
  const fallbackImage = 'https://i.pinimg.com/736x/df/12/4a/df124abf279de0e5291891691e1c9bed.jpg';

  return (
    <div className="product-detail-page">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Home</Link> /
          <Link to={`/shop?category=${product.categoryId}`}>
            {product.category ? product.category.charAt(0).toUpperCase() + product.category.slice(1) : "All"} Medications
          </Link>{" "}
          /<span className="current">{product.name}</span>
        </div>

        <div className="product-detail-container">
          <div className="product-detail-left">
            <div className="product-image-container">
              {product.badge && (
                <span
                  className={`product-badge badge-${product.badge.toLowerCase()}`}
                >
                  {product.badge}
                </span>
              )}
              <img
                src={product.image || fallbackImage}
                alt={product.name}
                className="product-detail-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = fallbackImage;
                }}
              />
            </div>
          </div>

          <div className="product-detail-right">
            <h1 className="product-title">{product.name}</h1>

            <div className="product-manufacturer">
              <span className="manufacturer-label">Manufacturer: </span>
              <span className="manufacturer-name">{product.manufacturer}</span>
            </div>

            <div className="product-detail-price">
              {product.oldPrice && (
                <span className="old-price">
                  €{product.oldPrice.toFixed(2)}
                </span>
              )}
              <span className="price">€{product.price.toFixed(2)}</span>
            </div>

            <div className="product-availability">
              <span className="availability-label">Availability: </span>
              {product.stock > 0 ? (
                <span className="in-stock">In stock</span>
              ) : (
                <span className="out-of-stock">Out of stock</span>
              )}
            </div>

            {/* <div className="product-summary">
              <p>{product.description}</p>
            </div> */}

            {product.stock > 0 && (
              <div className="product-actions">
                <div className="quantity-selector">
                  <label htmlFor="quantity">Quantity:</label>
                  <div className="quantity-input-group">
                    <button
                      className="quantity-btn minus"
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      id="quantity"
                      name="quantity"
                      min="1"
                      value={quantity}
                      onChange={handleQuantityChange}
                      className="quantity-input"
                    />
                    <button
                      className="quantity-btn plus"
                      onClick={incrementQuantity}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="button-group">
                  <button
                    className="btn btn-cart add-to-cart-btn"
                    onClick={handleAddToCart}
                    disabled={cartLoading}
                  >
                    <FaShoppingCart className="btn-icon" />
                    {cartLoading ? "ADDING..." : "ADD TO CART"}
                  </button>
                  <Link
                    to={`/quote?product=${id}`}
                    className="btn btn-quote quote-btn"
                  >
                    <FaRegFileAlt className="btn-icon" />
                    ADD TO QUOTE
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="product-tabs">
          <div className="tabs-header">
            <button
              className={`tab-btn ${activeTab === "description" ? "active" : ""}`}
              onClick={() => setActiveTab("description")}
            >
              DESCRIPTION
            </button>
            <button
              className={`tab-btn ${activeTab === "additional" ? "active" : ""}`}
              onClick={() => setActiveTab("additional")}
            >
              ADDITIONAL INFORMATION
            </button>
            <button
              className={`tab-btn ${activeTab === "reviews" ? "active" : ""}`}
              onClick={() => setActiveTab("reviews")}
            >
              REVIEWS (0)
            </button>
            <button
              className={`tab-btn ${activeTab === "shipping" ? "active" : ""}`}
              onClick={() => setActiveTab("shipping")}
            >
              SHIPPING & DELIVERY
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "description" && (
              <div className="tab-pane">
                <p>{product.description}</p>
                <ul className="product-features">
                  {product.features && product.features.map((feature, index) => (
                    <li key={index} className="feature-item">
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {activeTab === "additional" && (
              <div className="tab-pane">
                <h3>Additional Information</h3>
                <table className="additional-info-table">
                  <tbody>
                    <tr>
                      <th>Manufacturer</th>
                      <td>{product.manufacturer}</td>
                    </tr>
                    <tr>
                      <th>Category</th>
                      <td>
                        {category.name ? 
                          category.name.charAt(0).toUpperCase() + category.name.slice(1) : 
                          "Uncategorized"}
                      </td>
                    </tr>
                    <tr>
                      <th>SKU</th>
                      <td>LP-{product.id.toString().padStart(4, "0")}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="tab-pane">
                <h3>Customer Reviews</h3>
                <p>There are no reviews yet.</p>
                <button className="btn btn-secondary write-review-btn">
                  WRITE A REVIEW
                </button>
              </div>
            )}

            {activeTab === "shipping" && (
              <div className="tab-pane">
                <h3>Shipping & Delivery</h3>
                <p>
                  Prime Pharmacy delivers to most locations across the
                  United States. Delivery times typically range from 3-5 days.
                  For eligible prescriptions, we offer expedited shipping
                  options to ensure you never run low on your medications.
                </p>
                <p>
                  All orders are shipped in discreet, unlabeled packaging to
                  protect your privacy.
                </p>
              </div>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="related-products">
            <h2>Related Products</h2>
            <ProductGrid products={relatedProducts} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;



